import {
  ChatQuestionRenderProps,
  CircleOrCrossOutStatements,
  ContentIsScamOrNot,
  CorrectOrIncorrect,
  CorrectlyOrderRenderProps,
  MatchStatementsQuestion,
  Question,
  SanityQuestion,
  SearchResultRenderProps,
  SeveralOptionRenderProps,
} from "@common";
import {
  localizedPortableTextToTranslationData,
  localizedStringArrayToTranslationData,
} from "../translation";

export function transformSanityQuestionBaseProps(
  question: SanityQuestion
): Pick<
  Question,
  | "id"
  | "fitsAges"
  | "fitsProfiles"
  | "badge"
  | "title"
  | "desc"
  | "correctResultTitle"
  | "correctResultDescInnerHTML"
  | "incorrectResultTitle"
  | "incorrectResultDescInnerHTML"
  | "readMoreInnerHTML"
  | "youtubeVideoID"
> {
  return {
    id: {
      en: question._id,
      sv: question._id,
    },
    fitsAges: question.questionBase.fitsAges,
    fitsProfiles: question.questionBase.fitsProfiles,
    badge: "passwords",
    title: localizedStringArrayToTranslationData(question.questionBase.title),
    desc: localizedStringArrayToTranslationData(question.questionBase.description),
    correctResultTitle: localizedStringArrayToTranslationData(
      question.questionBase.correctResultTitle
    ),
    correctResultDescInnerHTML: localizedPortableTextToTranslationData(
      question.questionBase.correctResultDesc
    ),
    incorrectResultTitle: localizedStringArrayToTranslationData(
      question.questionBase.incorrectResultTitle
    ),
    incorrectResultDescInnerHTML: localizedPortableTextToTranslationData(
      question.questionBase.incorrectResultDesc
    ),
    readMoreInnerHTML: localizedPortableTextToTranslationData(question.questionBase.readMore),
    youtubeVideoID: question.questionBase.youtubeVideo
      ? question.questionBase.youtubeVideo.split("watch?v=")[1]
      : undefined,
  };
}

export function transformSanityQuestionRenderProps(
  question: SanityQuestion
): Pick<Question, "renderTemplate" | "renderProps"> {
  switch (question._type) {
    case "searchEngineQuestion": {
      const renderProps: SearchResultRenderProps = {
        query: localizedStringArrayToTranslationData(question?.renderProps.query),
        correctLinkIndex: (question?.renderProps.answers.findIndex((answer) => answer.correct) +
          1) as 1 | 2 | 3,
        link1MetaDesc: localizedStringArrayToTranslationData(
          question?.renderProps.answers[0]?.description
        ),
        link1Title: localizedStringArrayToTranslationData(question?.renderProps.answers[0].title),
        link1Type: question?.renderProps.answers[0].type,
        link1URL: localizedStringArrayToTranslationData(question?.renderProps.answers[0].url),
        link2MetaDesc: localizedStringArrayToTranslationData(
          question?.renderProps.answers[1]?.description
        ),
        link2Title: localizedStringArrayToTranslationData(question?.renderProps.answers[1].title),
        link2Type: question?.renderProps.answers[1].type,
        link2URL: localizedStringArrayToTranslationData(question?.renderProps.answers[1].url),
        link3MetaDesc: localizedStringArrayToTranslationData(
          question?.renderProps.answers[2]?.description
        ),
        link3Title: localizedStringArrayToTranslationData(question?.renderProps.answers[2].title),
        link3Type: question?.renderProps.answers[2].type,
        link3URL: localizedStringArrayToTranslationData(question?.renderProps.answers[2].url),
      };
      return {
        renderTemplate: "search-engine-results",
        renderProps,
      };
    }
    case "multichoiceQuestion": {
      const renderProps: SeveralOptionRenderProps = {
        option1Text: localizedStringArrayToTranslationData(question?.renderProps.options[0].text),
        option2Text: localizedStringArrayToTranslationData(question?.renderProps.options[1].text),
        option3Text: localizedStringArrayToTranslationData(question?.renderProps.options[2].text),
        option4Text: localizedStringArrayToTranslationData(question?.renderProps.options[3].text),
        option1IsCorrect: question?.renderProps.options[0].correct ? "correct" : "incorrect",
        option2IsCorrect: question?.renderProps.options[1].correct ? "correct" : "incorrect",
        option3IsCorrect: question?.renderProps.options[2].correct ? "correct" : "incorrect",
        option4IsCorrect: question?.renderProps.options[3].correct ? "correct" : "incorrect",
      };
      return {
        renderTemplate: "several-option",
        renderProps,
      };
    }
    case "chatAnswerQuestion": {
      const renderProps: ChatQuestionRenderProps = {
        yourAnswer: localizedStringArrayToTranslationData(question?.renderProps.yourAnswer),
        from: localizedStringArrayToTranslationData(question?.renderProps.from),
        type: question?.renderProps.type,
        text1SideText: localizedStringArrayToTranslationData(
          question?.renderProps.messages[0].text
        ),
        text1Side: question?.renderProps.messages[0].side,
        text2SideText: localizedStringArrayToTranslationData(
          question?.renderProps.messages[1].text
        ),
        text2Side: question?.renderProps.messages[1].side,
        text3SideText: localizedStringArrayToTranslationData(
          question?.renderProps.messages[2].text
        ),
        text3Side: question?.renderProps.messages[2].side,
        text4SideText: localizedStringArrayToTranslationData(
          question?.renderProps.messages[3].text
        ),
        text4Side: question?.renderProps.messages[3].side,
        answerOption1Text: localizedStringArrayToTranslationData(
          question?.renderProps.answerOptions[0].text
        ),
        answerOption1IsCorrect: question?.renderProps.answerOptions[0].correct
          ? "correct"
          : "incorrect",
        answerOption2Text: localizedStringArrayToTranslationData(
          question?.renderProps.answerOptions[1].text
        ),
        answerOption2IsCorrect: question?.renderProps.answerOptions[1].correct
          ? "correct"
          : "incorrect",
      };
      return {
        renderTemplate: "chat-answer",
        renderProps,
      };
    }
    case "orderingQuestion": {
      const renderProps: CorrectlyOrderRenderProps = {
        best: localizedStringArrayToTranslationData(question?.renderProps.best),
        worst: localizedStringArrayToTranslationData(question?.renderProps.worst),
        option1: localizedStringArrayToTranslationData(question?.renderProps.items[1].text),
        option2: localizedStringArrayToTranslationData(question?.renderProps.items[3].text),
        option3: localizedStringArrayToTranslationData(question?.renderProps.items[2].text),
        option4: localizedStringArrayToTranslationData(question?.renderProps.items[0].text),
        correctIdOrder: "4,1,3,2",
      };
      return {
        renderTemplate: "order-correctly",
        renderProps,
      };
    }
    case "circleOrCrossOutQuestion": {
      const renderProps: CircleOrCrossOutStatements = {
        mode: question?.renderProps.mode,
        statement1Text: localizedStringArrayToTranslationData(
          question?.renderProps.statements[0].text
        ),
        statement1IsCorrect: question?.renderProps.statements[0].correct ? "correct" : "incorrect",
        statement2Text: localizedStringArrayToTranslationData(
          question?.renderProps.statements[1].text
        ),
        statement2IsCorrect: question?.renderProps.statements[1].correct ? "correct" : "incorrect",
        statement3Text: localizedStringArrayToTranslationData(
          question?.renderProps.statements[2].text
        ),
        statement3IsCorrect: question?.renderProps.statements[2].correct ? "correct" : "incorrect",
        statement4Text: localizedStringArrayToTranslationData(
          question?.renderProps.statements[3].text
        ),
        statement4IsCorrect: question?.renderProps.statements[3].correct ? "correct" : "incorrect",
        statement5Text: localizedStringArrayToTranslationData(
          question?.renderProps.statements[4].text
        ),
        statement5IsCorrect: question?.renderProps.statements[4].correct ? "correct" : "incorrect",
        statement6Text: localizedStringArrayToTranslationData(
          question?.renderProps.statements[5].text
        ),
        statement6IsCorrect: question?.renderProps.statements[5].correct ? "correct" : "incorrect",
      };
      return {
        renderTemplate: "circle-or-cross-out",
        renderProps,
      };
    }
    case "matchStatementsQuestion": {
      const renderProps: MatchStatementsQuestion = {
        pair1TextLeft: localizedStringArrayToTranslationData(
          question?.renderProps.statements[0].textLeft
        ),
        pair1TextRight: localizedStringArrayToTranslationData(
          question?.renderProps.statements[0].textRight
        ),
        pair2TextLeft: localizedStringArrayToTranslationData(
          question?.renderProps.statements[1].textLeft
        ),
        pair2TextRight: localizedStringArrayToTranslationData(
          question?.renderProps.statements[1].textRight
        ),
        pair3TextLeft: localizedStringArrayToTranslationData(
          question?.renderProps.statements[2].textLeft
        ),
        pair3TextRight: localizedStringArrayToTranslationData(
          question?.renderProps.statements[2].textRight
        ),
        pair4TextLeft: localizedStringArrayToTranslationData(
          question?.renderProps.statements[3].textLeft
        ),
        pair4TextRight: localizedStringArrayToTranslationData(
          question?.renderProps.statements[3].textRight
        ),
      };
      return {
        renderTemplate: "match-statements",
        renderProps,
      };
    }
    case "scamOrNotQuestion": {
      const renderProps: ContentIsScamOrNot = {
        content: localizedStringArrayToTranslationData(question?.renderProps.content),
        isScam: question?.renderProps.isScam ? "correct" : "incorrect",
        from: question?.renderProps.from,
        type: question?.renderProps.type,
      };
      return {
        renderTemplate: "content-is-scam-or-not",
        renderProps,
      };
    }
    case "passwordStrengthQuestion": {
      const renderProps = {
        profileBasedServiceName: question?.renderProps.profileBasedServiceName,
        profileBasedServiceColor: question?.renderProps.profileBasedServiceColor,
        profileBasedServiceSecondColor: question?.renderProps.profileBasedServiceSecondColor,
        profileBasedServiceThirdColor: question?.renderProps.profileBasedServiceThirdColor,
      };
      return {
        renderTemplate: "test-password-strength",
        renderProps,
      };
    }
    case "whoIsRightQuestion": {
      const renderProps = {
        person1Name: question?.renderProps.person1Name,
        person1ImageSrcName: question?.renderProps.person1ImageSrcName,
        person1Text: localizedStringArrayToTranslationData(question?.renderProps.person1Text),
        person1IsCorrect: (question?.renderProps.correctPerson === "person1"
          ? "correct"
          : "incorrect") as CorrectOrIncorrect,
        person2Name: question?.renderProps.person2Name,
        person2ImageSrcName: question?.renderProps.person2ImageSrcName,
        person2Text: localizedStringArrayToTranslationData(question?.renderProps.person2Text),
        person2IsCorrect: (question?.renderProps.correctPerson === "person2"
          ? "correct"
          : "incorrect") as CorrectOrIncorrect,
      };
      return {
        renderTemplate: "who-is-right",
        renderProps,
      };
    }
    case "yesNoQuestion": {
      return {
        renderTemplate: "yes-no",
        renderProps: {
          yesScore: question?.renderProps.yesIsCorrect ? "correct" : "incorrect",
          noScore: question?.renderProps.yesIsCorrect ? "incorrect" : "correct",
        },
      };
    }
    case "fakeDomainQuestion": {
      return {
        renderTemplate: "fake-domain",
      };
    }
    case "fakeWebsiteQuestion": {
      return {
        renderTemplate: "fake-website",
        renderProps: {
          websiteHTML: question?.renderProps.websiteHTML,
        },
      };
    }
    case "imageScamQuestion":
      return {
        renderTemplate: "image-is-scam-or-not",
        renderProps: {
          imageSrc: question?.imageRenderProps.image.asset.url,
          leftButtonText: localizedStringArrayToTranslationData(
            question?.renderProps.leftButtonText
          ),
          rightButtonText: localizedStringArrayToTranslationData(
            question?.renderProps.rightButtonText
          ),
          rightButtonIsCorrect: question?.renderProps.rightButtonIsCorrect
            ? "correct"
            : "incorrect",
          leftButtonIsCorrect: question?.renderProps.rightButtonIsCorrect ? "incorrect" : "correct",
        },
      };
    default:
      throw new Error(`Unknown question type: ${question}`);
  }
}

export function transformSanityQuestion(
  question: SanityQuestion
): Omit<Question, "relatedQuizModules" | "variant"> {
  return {
    ...transformSanityQuestionBaseProps(question),
    ...transformSanityQuestionRenderProps(question),
  };
}
